import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useNotificationStore } from "../../../Zustand/notification.store";
import { useApiCreateMunicipalityRequest } from "../../../API/municipalities.api";
import { useAuthStore } from "../../../Zustand/auth.store";
import { NewMunicipalityRequest } from "../../../API/types/municipalities.types";
import { EC2Municipality } from "../../../API/types/documentationCatalogs.types";

interface ManageMunicipalityModalProps {
  open: boolean;
  onClose: () => void;
  municipality: EC2Municipality | null;
}

const MAX_COMMENT_LENGTH = 300;

const ManageMunicipalityModal = ({
  open,
  onClose,
  municipality,
}: ManageMunicipalityModalProps) => {
  /**
   * Custom hooks
   */
  const showNotification = useNotificationStore((s) => s.show);
  const workdayUsername = useAuthStore((s) => s.workdayUsername);

  /**
   * States
   */
  const [action, setAction] = useState<"update" | "delete" | "">("");
  const [newName, setNewName] = useState(municipality?.municipalityName || "");
  const [comments, setComments] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState<string>("");

  /**
   * Helpers
   */
  const isValidUrl = (url: string): boolean => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  /**
   * API
   */
  const {
    call: callCreateMunicipalityRequest,
    loading: createMunicipalityRequestLoading,
  } = useApiCreateMunicipalityRequest({
    onCompleted: () => {
      showNotification(
        `Municipality ${action} request submitted successfully`,
        "success"
      );
      onClose();
    },
    onError: () => {
      showNotification("Request failed, please try again", "error");
    },
  });

  const handleRequest = () => {
    if (municipality?.stateId) {
      const requestData: NewMunicipalityRequest = {
        municipalityId: municipality?.municipalityId,
        requestedMunicipalityName: newName,
        originalMunicipalityName: municipality?.municipalityName || "",
        stateId: municipality?.stateId,
        stateName: municipality?.name || "",
        userName: workdayUsername || "",
        comment: comments,
        action: action || "update",
        websiteUrl: action === "update" ? websiteUrl : undefined,
        isState: false,
      };

      callCreateMunicipalityRequest([requestData]);
    }
  };

  const isRequestDisabled =
    !action ||
    !comments.trim() ||
    comments.length > MAX_COMMENT_LENGTH ||
    (action === "update" && websiteUrl !== "" && !isValidUrl(websiteUrl));

  /**
   * Effects
   */
  useEffect(() => {
    if (open) {
      setAction("");
      setNewName(municipality?.municipalityName || "");
      setComments("");
      setWebsiteUrl("");
    }
  }, [open, municipality]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Manage Municipality</DialogTitle>
      <DialogContent>
        <div className="mb-2">
          <span className="font-semibold">
            {municipality
              ? `Managing municipality: ${municipality.municipalityName}`
              : "No municipality selected."}
          </span>
        </div>
        <RadioGroup
          row
          value={action}
          onChange={(e) => setAction(e.target.value as "update" | "delete")}
          className="mb-2"
        >
          <FormControlLabel value="update" control={<Radio />} label="Update" />
          <FormControlLabel value="delete" control={<Radio />} label="Remove" />
        </RadioGroup>
        {action === "update" && (
          <>
            <TextField
              sx={{ marginY: "1rem" }}
              label="Website URL (Optional)"
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              placeholder="Website URL (Optional)"
              fullWidth
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              error={websiteUrl !== "" && !isValidUrl(websiteUrl)}
              helperText={
                websiteUrl !== "" && !isValidUrl(websiteUrl)
                  ? "Please enter a valid URL"
                  : ""
              }
            />
          </>
        )}
        <div className="my-4">
          <label htmlFor="comments" className="block text-gray-500 mb-1">
            Comments
          </label>

          <textarea
            className="w-full border border-gray-300 rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
            rows={3}
            maxLength={MAX_COMMENT_LENGTH}
            placeholder="Comments (required)"
            value={comments}
            onChange={(e) => setComments(e.target.value)}
          />
          <div className="text-right text-xs text-gray-500 mt-1">
            {comments.length} / {MAX_COMMENT_LENGTH}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <div className="flex w-full justify-end space-x-2">
          <Button onClick={onClose} color="primary" variant="text">
            Close
          </Button>

          <Button
            onClick={handleRequest}
            color="primary"
            variant="contained"
            disabled={isRequestDisabled || createMunicipalityRequestLoading}
          >
            {createMunicipalityRequestLoading ? (
              <CircularProgress size={24} sx={{ color: "white" }} />
            ) : (
              "Request"
            )}
          </Button>
        </div>
      </DialogActions>
    </Dialog>
  );
};

export default ManageMunicipalityModal;
